import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sparkles, Save, Check, RefreshCw, AlertCircle, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');

const POLL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000; // give up after 60s — Claude is hung, not slow

/**
 * FounderLetterEditor — master admin only.
 * • Fetches the current week's letter
 * • "Generate draft" kicks off a backend async task (HTTP 202) and then
 *   polls /current every 2s until the letter shows up. This pattern is
 *   resilient against the production ingress 502 timeout that fired when
 *   the synchronous LLM call exceeded the gateway window.
 * • Inline textarea to edit
 * • Approve = marks the letter for inclusion in the Monday Investor Pulse
 */
const FounderLetterEditor = () => {
  const [letter, setLetter] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [body, setBody] = useState('');
  const [flash, setFlash] = useState(null);
  const pollRef = useRef(null);
  const pollDeadlineRef = useRef(0);
  const baselineGeneratedAtRef = useRef(null);

  const clearPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // `load` and `pollOnce` are mutually referential — `load` needs to
  // schedule polling with `pollOnce`, and `pollOnce` calls back into
  // state setters. We break the circular dep with a stable ref that
  // holds the latest `pollOnce`, so `load` can dispatch through it
  // without listing `pollOnce` (which would cause useCallback deps
  // to invalidate every render and cascade re-registrations of the
  // polling interval).
  const pollOnceRef = useRef(null);

  const load = useCallback(async () => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    try {
      const r = await fetch(`${API}/api/admin/founder-letter/current`, { headers: { 'Authorization': `Bearer ${t}` } });
      if (r.status === 403) { setError('not_master'); setLoading(false); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setLetter(d.letter);
      setBody(d.letter?.body || '');
      setGenerating(Boolean(d.generating));
      setError(null);
      // If we land on a page that's already generating (e.g. tab reload), resume polling.
      if (d.generating && !pollRef.current) {
        baselineGeneratedAtRef.current = d.letter?.generated_at || null;
        pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
        pollRef.current = setInterval(() => pollOnceRef.current?.(), POLL_MS);
      }
      return d;
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const pollOnce = useCallback(async () => {
    if (Date.now() > pollDeadlineRef.current) {
      clearPolling();
      setBusy(false);
      setGenerating(false);
      setFlash({ err: 'Generation took longer than 60s — try again or check the LLM provider status.' });
      return;
    }
    try {
      const r = await fetch(`${API}/api/admin/founder-letter/current`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
      if (!r.ok) return; // transient blip, keep polling
      const d = await r.json();
      const newGenAt = d.letter?.generated_at;
      const baseline = baselineGeneratedAtRef.current;
      const hasNewLetter = d.letter && newGenAt && newGenAt !== baseline;

      // Success: a NEW letter is now stored OR backend says generating finished without error.
      if (hasNewLetter || (!d.generating && d.letter && !d.generation_error)) {
        clearPolling();
        setLetter(d.letter);
        setBody(d.letter.body);
        setGenerating(false);
        setBusy(false);
        setFlash({ ok: 'New draft generated via Claude Sonnet 4.5.' });
        return;
      }
      // Failure: backend reports an explicit generation error.
      if (d.generation_error) {
        clearPolling();
        setGenerating(false);
        setBusy(false);
        setFlash({ err: `Generation failed: ${d.generation_error}` });
        return;
      }
      // Still generating — update state and keep waiting.
      setGenerating(Boolean(d.generating));
    } catch {
      // Network blip; the next tick will retry. Don't kill the polling.
    }
  }, [clearPolling]);

  // Keep the ref in sync with the latest `pollOnce` on every render.
  // `load` (declared above) dispatches through this ref so it doesn't
  // need to list `pollOnce` in its deps → avoids a callback-rebuild
  // cascade that would re-register the polling interval.
  pollOnceRef.current = pollOnce;

  useEffect(() => { load(); return clearPolling; }, [load, clearPolling]);

  const generate = async () => {
    if (letter && !window.confirm('Regenerate this week\'s letter? The current draft will be overwritten.')) return;
    setBusy(true); setFlash(null);
    baselineGeneratedAtRef.current = letter?.generated_at || null;
    try {
      const r = await fetch(`${API}/api/admin/founder-letter/generate`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      // Accept both 200 (legacy synchronous shape) and 202 (new async shape).
      if (r.status !== 202 && !r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();

      // If the backend returned the legacy synchronous payload, use it directly.
      if (d.letter && d.letter.body) {
        setLetter(d.letter);
        setBody(d.letter.body);
        setBusy(false);
        setFlash({ ok: 'New draft generated via Claude Sonnet 4.5.' });
        return;
      }

      // Otherwise we got 202 → enter polling mode. Dispatch through the
      // ref so this handler doesn't need to close over `pollOnce`.
      setGenerating(true);
      pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
      clearPolling();
      pollRef.current = setInterval(() => pollOnceRef.current?.(), POLL_MS);
    } catch (e) {
      setBusy(false);
      setGenerating(false);
      setFlash({ err: e.message });
    }
  };

  const save = async (status) => {
    if (!letter) return;
    setBusy(true); setFlash(null);
    try {
      const r = await fetch(`${API}/api/admin/founder-letter/save`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ letter_id: letter.letter_id, body, status }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setLetter(d.letter);
      setFlash({ ok: status === 'approved' ? 'Approved. Monday\'s Investor Pulse will include this letter.' : 'Draft saved.' });
    } catch (e) {
      setFlash({ err: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (error === 'not_master') return null;
  if (loading) return <div className="bg-white rounded-lg border p-4 animate-pulse" data-testid="founder-letter-loading" style={{ borderColor: '#E0E0E0' }}><div className="h-4 w-40 bg-gray-200 rounded" /></div>;

  const isApproved = letter?.status === 'approved';

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="founder-letter-card" style={{ borderColor: '#E0E0E0' }}>
      <div className="px-5 py-4 border-b flex items-center justify-between flex-wrap gap-2" style={{ borderColor: '#F0F0F0', background: 'linear-gradient(135deg, rgba(245,131,31,0.06), rgba(123,79,216,0.04))' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F5831F', color: 'white' }}>
            <Sparkles size={18} />
          </div>
          <div>
            <h3 className="text-base font-black" style={{ color: '#1A1209' }}>Founder&apos;s Letter</h3>
            <p className="text-[11px] text-gray-500">
              AI-drafted weekly narrative for the Investor Pulse · Claude Sonnet 4.5
              {letter && <> · <b>{letter.week}</b> · <span className={isApproved ? 'text-green-700' : 'text-orange-700'}>{isApproved ? 'Approved' : 'Draft'}</span></>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={generate} disabled={busy || generating}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors hover:bg-orange-50 disabled:opacity-50"
                  style={{ borderColor: '#F5831F', color: '#F5831F' }}
                  data-testid="founder-letter-generate">
            {(busy || generating) ? <RefreshCw size={12} className="inline animate-spin mr-1" /> : <Sparkles size={12} className="inline mr-1" />}
            {generating ? 'Drafting…' : (letter ? 'Regenerate' : 'Generate draft')}
          </button>
        </div>
      </div>

      <div className="p-5">
        {flash && (
          <div className={`mb-3 px-3 py-2 rounded-lg text-xs font-semibold ${flash.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {flash.ok ? <><Check size={12} className="inline mr-1" />{flash.ok}</> : <><AlertCircle size={12} className="inline mr-1" />{flash.err}</>}
          </div>
        )}

        {!letter && !generating && (
          <div className="text-center py-8 text-sm text-gray-500" data-testid="founder-letter-empty">
            No letter yet this week. Click <b>Generate draft</b> above to have Claude write a 3-paragraph narrative based on the live metrics delta.
          </div>
        )}

        {!letter && generating && (
          <div className="text-center py-8 text-sm text-gray-600" data-testid="founder-letter-generating">
            <RefreshCw size={18} className="inline animate-spin mr-2 text-orange-500" />
            Drafting via Claude Sonnet 4.5… this usually takes 10–15 seconds. The card will update automatically when the letter is ready.
          </div>
        )}

        {letter && (
          <>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="w-full text-sm leading-relaxed px-3 py-2 border rounded-lg font-serif focus:outline-none focus:ring-2"
              style={{ borderColor: '#E0E0E0', color: '#1A1209', fontFamily: "Georgia, 'Times New Roman', serif" }}
              data-testid="founder-letter-textarea"
              placeholder="Your founder's letter for this week..."
            />

            <div className="mt-3 flex flex-wrap items-center gap-2 justify-between">
              <div className="text-[11px] text-gray-500">
                {body.length} chars · {body.trim().split(/\s+/).filter(Boolean).length} words
              </div>
              <div className="flex items-center gap-2">
                <a href={`${API}/api/admin/investor-pulse/preview`} target="_blank" rel="noopener noreferrer"
                   className="text-[11px] font-semibold text-gray-600 hover:text-gray-900 underline flex items-center gap-1"
                   data-testid="founder-letter-preview">
                  <Mail size={11} /> Preview full email
                </a>
                <button onClick={() => save('draft')} disabled={busy}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-50"
                        style={{ borderColor: '#E0E0E0', color: '#333' }}
                        data-testid="founder-letter-save-draft">
                  <Save size={12} className="inline mr-1" />Save draft
                </button>
                <button onClick={() => save('approved')} disabled={busy}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                        style={{ backgroundColor: isApproved ? '#2EAA5C' : '#F5831F', color: 'white' }}
                        data-testid="founder-letter-approve">
                  <Check size={12} className="inline mr-1" />
                  {isApproved ? 'Approved' : 'Approve for Monday'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FounderLetterEditor;
